/**
 * FormsPage Component
 * Main forms page — browse, download, view, and order forms.
 *
 * Tabs:
 * - Forms: view and download non-orderable forms
 * - Supplies: orderable forms with shopping cart
 * - Order History: past orders with detail drill-down
 *
 * Features:
 * - Auto-defaults to user's main agency
 * - Inline PDF viewing via modal
 * - Full mobile-responsive design
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  DocumentTextIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

import { useOathAuth } from '../../../services/oathAuthContext';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathAgencyInfo,
  OathAlert,
} from '../../shared/OathUI';
import { getForms, downloadForm } from '../../../services/oathFormsService';

import FormsTable from './FormsTable';
import FormViewerModal from './FormViewerModal';
import ShoppingCartModal from './ShoppingCartModal';
import OrderHistoryTab from './OrderHistoryTab';

const FormsPage = () => {
  const { user } = useOathAuth();

  // Agency selection
  const agencies = useMemo(() => user?.agencies || [], [user]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);

  // Set default agency on mount - prefer main agency, then canPost, then first
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId]);

  const selectedAgency = useMemo(
    () => agencies.find(a => a.agencyId === selectedAgencyId),
    [agencies, selectedAgencyId]
  );

  // Form data
  const [allForms, setAllForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derived list: view-only forms (non-orderable)
  const viewOnlyForms = useMemo(() => allForms.filter((f) => !f.orderable), [allForms]);
  const supplyForms = useMemo(() => allForms.filter((f) => f.orderable), [allForms]);

  const [cart, setCart] = useState(() => new Map());
  const [cartOpen, setCartOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const cartItemCount = useMemo(() => {
    let count = 0;
    cart.forEach((qty) => { if (qty > 0) count++; });
    return count;
  }, [cart]);

  // Modal state
  const [viewerForm, setViewerForm] = useState(null);

  // Fetch forms
  const fetchForms = useCallback(async () => {
    if (!selectedAgencyId) return;

    setLoading(true);
    setError(null);

    const result = await getForms(selectedAgencyId);

    if (result.success) {
      const data = result.data || {};
      setAllForms([...(data.stateFormList || []), ...(data.bankersFormList || [])]);
    } else {
      setError(result.error || 'Failed to load forms');
      setAllForms([]);
    }
    setLoading(false);
  }, [selectedAgencyId]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleQuantityChange = useCallback((formId, quantity) => {
    setCart((prev) => {
      const next = new Map(prev);
      if (quantity <= 0) { next.delete(formId); } else { next.set(formId, quantity); }
      return next;
    });
  }, []);
  const handleRemoveItem = useCallback((formId) => {
    setCart((prev) => { const next = new Map(prev); next.delete(formId); return next; });
  }, []);
  const handleClearCart = useCallback(() => { setCart(new Map()); }, []);

  // View PDF
  const handleViewForm = useCallback((form) => {
    setViewerForm(form);
  }, []);

  // Download form
  const handleDownloadForm = useCallback(async (form) => {
    const result = await downloadForm(form.formId);
    if (result.success) {
      const url = window.URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName || form.fileName || `${form.formName || 'form'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Form downloaded');
    } else {
      toast.error(result.error || 'Failed to download form');
    }
  }, []);

  const TabBadge = ({ count }) => {
    if (!count) return null;
    return (
      <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary dark:bg-primary-light/10 dark:text-primary-light">
        {count}
      </span>
    );
  };

  return (
    <OathPageWrapper>
      {/* Header */}
      <OathHeaderCard
        title="Forms"
        subtitle="Browse, download, and order forms"
        icon={DocumentTextIcon}
        rightContent={
          selectedAgency ? <OathAgencyInfo agency={selectedAgency} /> : null
        }
      />

      {/* Content */}
      <OathContentCard noPadding>
        {error && !loading ? (
          <div className="p-6">
            <OathAlert variant="error" message={error} />
          </div>
        ) : (
          <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
            <TabList className="flex border-b border-gray-200 dark:border-gray-700 px-4">
              <Tab className="px-4 py-3 text-sm font-medium ui-selected:text-primary ui-selected:border-b-2 ui-selected:border-primary text-gray-500 dark:text-gray-400 focus:outline-none">
                Forms
              </Tab>
              <Tab className="px-4 py-3 text-sm font-medium ui-selected:text-primary ui-selected:border-b-2 ui-selected:border-primary text-gray-500 dark:text-gray-400 focus:outline-none flex items-center">
                Supplies
                <TabBadge count={cartItemCount} />
              </Tab>
              <Tab className="px-4 py-3 text-sm font-medium ui-selected:text-primary ui-selected:border-b-2 ui-selected:border-primary text-gray-500 dark:text-gray-400 focus:outline-none">
                Order History
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <FormsTable
                  forms={viewOnlyForms}
                  onView={handleViewForm}
                  onDownload={handleDownloadForm}
                  loading={loading}
                  showOrderColumns={false}
                />
              </TabPanel>
              <TabPanel>
                <FormsTable
                  forms={supplyForms}
                  onView={handleViewForm}
                  onDownload={handleDownloadForm}
                  loading={loading}
                  showOrderColumns={true}
                  cart={cart}
                  onQuantityChange={handleQuantityChange}
                />
              </TabPanel>
              <TabPanel>
                <OrderHistoryTab agencyId={selectedAgencyId} />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        )}
      </OathContentCard>

      {/* Floating cart button */}
      {cartItemCount > 0 && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="fixed bottom-6 right-6 z-30 flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-5 py-3 rounded-2xl shadow-xl shadow-primary/30 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 group"
        >
          <ShoppingCartIcon className="h-5 w-5" />
          <span className="text-sm font-semibold">Cart</span>
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-xs font-bold">
            {cartItemCount}
          </span>
        </button>
      )}

      {/* PDF Viewer Modal */}
      <FormViewerModal
        isOpen={viewerForm !== null}
        onClose={() => setViewerForm(null)}
        form={viewerForm}
        agencyId={selectedAgencyId}
      />

      {/* Shopping Cart Modal */}
      <ShoppingCartModal
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        forms={allForms}
        onRemoveItem={handleRemoveItem}
        onClearCart={handleClearCart}
        agencyId={selectedAgencyId}
      />
    </OathPageWrapper>
  );
};

export default FormsPage;
